import { useEffect, useMemo, useRef } from 'react';
import {
  Checkbox,
  DatePicker,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Spin,
  Alert,
  Tag,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AppButton from '../AppButton';
import AppTypography from '../typography/Typography';
import { useFilterFields } from '../../hooks/useDropdownFields';
import { useDropdownValues } from '../../hooks/useDropdownValues';
import '../../styles/DynamicFieldFilter.css';

const DATE_SHORTCUTS = [
  { label: 'Last 7 days', getDates: () => [dayjs().subtract(6, 'day'), dayjs()] },
  { label: 'This month',  getDates: () => [dayjs().startOf('month'), dayjs()] },
  { label: 'Overdue',     getDates: () => [null, dayjs().subtract(1, 'day')] },
];

function DateRangeWithShortcuts({ value, onChange, disabled }) {
  return (
    <Flex vertical gap={4}>
      <DatePicker.RangePicker
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{ width: '100%' }}
        allowEmpty={[true, true]}
        disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
      />
      {!disabled && (
        <Space size={4} wrap>
          {DATE_SHORTCUTS.map(({ label, getDates }) => (
            <Tag
              key={label}
              className="filter-date-shortcut"
              onClick={() => onChange?.(getDates())}
            >
              {label}
            </Tag>
          ))}
        </Space>
      )}
    </Flex>
  );
}

const operatorOptions = [
  { label: 'AND', value: 'and' },
  { label: 'OR', value: 'or' },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Not Empty', value: 'notEmpty' },
];

const BOOLEAN_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

function NumberRangeInput({ value, onChange, disabled }) {
  const [min, max] = Array.isArray(value) ? value : [undefined, undefined];
  return (
    <Flex gap={8}>
      <InputNumber
        disabled={disabled}
        placeholder="Min"
        value={min}
        onChange={(v) => onChange?.([v, max])}
        style={{ flex: 1 }}
      />
      <InputNumber
        disabled={disabled}
        placeholder="Max"
        value={max}
        onChange={(v) => onChange?.([min, v])}
        style={{ flex: 1 }}
      />
    </Flex>
  );
}

const defaultFilterRow = { operator: 'and', field: undefined, values: [] };

function renderValuesInput({ filterInputType, fieldType, disabled, loading, valueOptions, onSearch, onOpen }) {
  // filterInputType from admin config takes precedence over fieldType-based defaults
  const effective = filterInputType
    || (fieldType === 'boolean' ? 'boolean' : null)
    || (fieldType === 'date' ? 'datepicker' : null)
    || (fieldType === 'number' ? 'number-range' : null)
    || 'dropdown';

  if (effective === 'boolean') {
    return (
      <Select
        allowClear
        disabled={disabled}
        mode="multiple"
        options={BOOLEAN_OPTIONS}
        placeholder="Select value"
      />
    );
  }

  if (effective === 'text') {
    return (
      <Input
        disabled={disabled}
        placeholder="Type keyword..."
        allowClear
      />
    );
  }

  if (effective === 'datepicker') {
    return <DateRangeWithShortcuts disabled={disabled} />;
  }

  if (effective === 'number-range') {
    return <NumberRangeInput disabled={disabled} />;
  }

  if (effective === 'checkbox') {
    return (
      <Spin spinning={loading}>
        <Checkbox.Group
          disabled={disabled}
          options={valueOptions}
          style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}
        />
      </Spin>
    );
  }

  if (effective === 'radio') {
    return (
      <Spin spinning={loading}>
        <Radio.Group
          disabled={disabled}
          options={valueOptions}
          style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}
        />
      </Spin>
    );
  }

  // dropdown (default)
  return (
    <Select
      allowClear
      disabled={disabled}
      loading={loading}
      mode="multiple"
      options={valueOptions}
      placeholder={disabled ? 'Select field first' : 'Select values'}
      showSearch
      filterOption={false}
      onSearch={onSearch}
      onDropdownVisibleChange={onOpen}
    />
  );
}

function getFieldLabel(field) {
  return field.label ?? field.fieldLabel ?? field.name ?? field.fieldName ?? field.value;
}

function getFieldValue(field) {
  return field.value ?? field.field ?? field.fieldName ?? field.name ?? field.label;
}

// Convert an applied filter row back to a form row value.
// Applied rows carry string dates and scalar values; the form needs dayjs objects
// for the date picker and raw values for all other inputs.
function appliedRowToFormRow(row, apiFields) {
  const meta = apiFields.find((f) => f.value === row.field);
  const filterInputType = row.filterInputType ?? meta?.filterInputType;
  const isDateInput = filterInputType === 'datepicker' || (!filterInputType && meta?.type === 'date');
  const isTextInput = filterInputType === 'text';

  let values = row.values ?? [];
  if (isTextInput) {
    values = row.value ?? '';
  } else if (isDateInput) {
    const from = row.from ? dayjs(row.from) : null;
    const to   = row.to   ? dayjs(row.to)   : null;
    values = [from, to];
  }

  return {
    field: row.field,
    operator: row.operator ?? 'and',
    values,
    locked: meta?.isDefaultFilter ?? false,
  };
}

export default function DynamicFieldFilter({
  moduleName,
  open = false,
  filters = [],
  onClose,
  onApply,
  onReset,
}) {
  const [form] = Form.useForm();
  const filterRows = Form.useWatch('filters', form) ?? [];

  // Fetch filter-visible fields from auth API (respects Configure → Filter tab)
  const { fields, loading: fieldsLoading, error: fieldsError } = useFilterFields(
    open ? moduleName : null,
  );

  const apiFields = useMemo(() => (
    (fields ?? [])
      .map((field) => ({
        label: getFieldLabel(field),
        value: getFieldValue(field),
        type: field.type,
        filterInputType: field.filterInputType,
        dataSource: field.dataSource,
        groupName: field.groupName,
        lookup: field.lookup ?? null,
        isDefaultFilter:
          field.isDefaultFilter === 1 ||
          field.isDefaultFilter === "1" ||
          field.isDefaultFilter === true,
      }))
      .filter((field) => field.label && field.value)
  ), [fields]);

  const defaultFields = useMemo(
    () => apiFields.filter((field) => field.isDefaultFilter),
    [apiFields],
  );
  const defaultFieldsSignature = useMemo(
    () => defaultFields.map((field) => String(field.value)).sort().join('|'),
    [defaultFields],
  );

  // Fields selectable via "Add Row" — default fields are still selectable here
  // (e.g. to combine the same field with a different operator); duplicate
  // selection is prevented separately via the usedFields check below.
  const addableFields = apiFields;

  const { valuesByField, loadingFields, fetchValues } = useDropdownValues(moduleName);

  function fetchInitialValuesIfNeeded(field) {
    const meta = apiFields.find((f) => f.value === field);
    const inputType = meta?.filterInputType;
    const noApiFetch = !field
      || inputType === 'datepicker'
      || inputType === 'number-range'
      || inputType === 'text'
      || (!inputType && ['boolean', 'date', 'number'].includes(meta?.type));
    if (!noApiFetch) {
      const isLookup = Boolean(meta?.lookup?.collection);
      fetchValues(field, '', {
        dataSource: isLookup ? 'lookup' : meta?.dataSource,
        groupName: meta?.groupName,
        lookup: meta?.lookup,
      });
    }
  }

  function buildDefaultRows() {
    return defaultFields.length > 0
      ? defaultFields.map((field) => ({
        operator: 'and',
        field: field.value,
        values: [],
        locked: field.isDefaultFilter,
      }))
      : [defaultFilterRow];
  }

  function mergeDefaultRows(rows = []) {
    const validRows = rows.filter((row) => row?.field);
    const existingFields = new Set(validRows.map((row) => String(row.field)));
    const missingDefaults = defaultFields
      .filter((field) => !existingFields.has(String(field.value)))
      .map((field) => ({
        operator: 'and',
        field: field.value,
        values: [],
        locked: true,
      }));

    const mergedRows = [...missingDefaults, ...validRows].map((row) => ({
      ...row,
      locked: defaultFields.some((field) => String(field.value) === String(row.field)),
    }));
    return mergedRows.length > 0 ? mergedRows : [defaultFilterRow];
  }

  // Tracks whether the last `filters` prop change originated from inside this
  // drawer (Apply / Reset). When true the external-sync effect below skips the
  // form update — the form is already correct and re-converting applied rows
  // back would produce stale dayjs objects for date fields.
  const isInternalChangeRef = useRef(false);

  // Sync the internal form when filters are changed from *outside* the drawer
  // (e.g. a chip is removed from the filter chips bar in ListView).
  const externalFiltersRef = useRef(filters);
  useEffect(() => {
    if (filters === externalFiltersRef.current) return;
    externalFiltersRef.current = filters;

    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    // Convert applied rows back to form rows and update the form.
    const formRows = (filters ?? [])
      .filter((row) => row.field)
      .map((row) => appliedRowToFormRow(row, apiFields));

    // External ListView updates (tab changes, chip removal, or an initial
    // empty filters array) must not wipe the admin-configured default rows.
    // This used to replace the form with a blank row after defaults had been
    // initialized, which is why the drawer could open without its defaults.
    const rows = mergeDefaultRows(formRows);
    form.setFieldsValue({ filters: rows });
    rows.forEach((row) => fetchInitialValuesIfNeeded(row.field));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, apiFields]);

  const initializedRef = useRef(false);
  const initializedDefaultsRef = useRef('');
  const prevModuleRef = useRef(moduleName);
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (moduleName !== prevModuleRef.current) {
      prevModuleRef.current = moduleName;
      initializedRef.current = false;
      initializedDefaultsRef.current = '';
      prevLoadingRef.current = false;
    }
  }, [moduleName]);
  useEffect(() => {
    // Initialize default rows only once per mount — not on every reopen.
    // The drawer stays mounted while closed (Drawer doesn't unmount its
    // content), so re-running this on each open would stomp on whatever
    // values the user had applied last time, resetting them back to blank.
    if (!open) return;
    const defaultsChanged = initializedDefaultsRef.current !== defaultFieldsSignature;
    if (initializedRef.current && !defaultsChanged) return;

    // Guard against the race on the very first open: this effect can run
    // before useFilterFields' own effect has flipped fieldsLoading to true,
    // so a stale "loading=false, fields=[]" snapshot would lock in the empty
    // fallback row forever. Only initialize once a load has actually
    // completed (loading transitioned true → false) or fields were already
    // available (e.g. a cached result from a prior open).
    const justFinishedLoading = prevLoadingRef.current && !fieldsLoading;
    const alreadyHasFields = !fieldsLoading && apiFields.length > 0;
    prevLoadingRef.current = fieldsLoading;
    if (!justFinishedLoading && !alreadyHasFields) return;

    const wasInitialized = initializedRef.current;
    initializedRef.current = true;
    initializedDefaultsRef.current = defaultFieldsSignature;
    const currentRows = form.getFieldValue('filters') ?? [];
    const rows = wasInitialized
      ? mergeDefaultRows(currentRows)
      : mergeDefaultRows(currentRows.length > 0 ? currentRows : buildDefaultRows());
    form.setFieldsValue({ filters: rows });
    rows.forEach((row) => fetchInitialValuesIfNeeded(row.field));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fieldsLoading, apiFields, defaultFieldsSignature]);

  function handleReset() {
    isInternalChangeRef.current = true;
    const rows = buildDefaultRows();
    form.setFieldsValue({ filters: rows });
    rows.forEach((row) => fetchInitialValuesIfNeeded(row.field));
    onApply?.({ moduleName, filters: [] });
    onReset?.();
    onClose?.();
  }

  function handleApply() {
    isInternalChangeRef.current = true;
    const raw = form.getFieldsValue();
    const appliedFilters = (raw.filters ?? [])
      .filter((row) => !isRowIncomplete(row))
      .map((formRow) => {
        const row = { ...formRow };
        delete row.locked;
        const meta = apiFields.find((f) => f.value === row.field);
        const fieldLabel = meta?.label ?? row.field;
        const filterInputType = meta?.filterInputType;
        const isDateInput = filterInputType === 'datepicker' || meta?.type === 'date';
        const isNumberRange = filterInputType === 'number-range'
          || (!filterInputType && meta?.type === 'number');
        const isTextInput = filterInputType === 'text';

        if (isDateInput) {
          const dateArr = Array.isArray(row.values) ? row.values : [];
          const [rawFrom, rawTo] = dateArr;
          const formatDate = (d) => {
            if (!d) return null;
            if (typeof d.format === 'function') return d.format('YYYY-MM-DD');
            if (typeof d.toISOString === 'function') return d.toISOString().substring(0, 10);
            if (d instanceof Date) return d.toISOString().substring(0, 10);
            return null;
          };
          const from = formatDate(rawFrom);
          const to = formatDate(rawTo);
          return {
            ...row,
            values: [from, to].filter(Boolean),
            from: from ?? undefined,
            to: to ?? undefined,
            fieldLabel,
            filterInputType,
          };
        }

        if (isNumberRange) {
          const [min, max] = Array.isArray(row.values) ? row.values : [];
          return {
            ...row,
            values: [min, max].filter((v) => v != null),
            from: min != null ? String(min) : undefined,
            to: max != null ? String(max) : undefined,
            fieldLabel,
            filterInputType,
          };
        }

        if (isTextInput) {
          const textVal = typeof row.values === 'string' ? row.values.trim() : '';
          return {
            ...row,
            value: textVal,
            values: [],
            fieldLabel,
            filterInputType,
          };
        }

        return { ...row, values: row.values ?? [], fieldLabel, filterInputType };
      });
    onApply?.({ moduleName, filters: appliedFilters });
    onClose?.();
  }

  function isRowIncomplete(row) {
    if (!row?.field) return true;
    if (row.operator === 'isEmpty' || row.operator === 'notEmpty') return false;
    const { values } = row;
    if (typeof values === 'string') return values.trim() === '';
    if (Array.isArray(values)) {
      return values.length === 0 || values.every((v) => v === null || v === undefined || v === '');
    }
    return true;
  }

  const hasNoCompleteRow = filterRows.length === 0 || filterRows.every(isRowIncomplete);

  function handleFieldChange(rowName, newField) {
    const currentFilters = form.getFieldValue('filters') ?? [];
    form.setFieldValue(
      'filters',
      currentFilters.map((row, index) => (index === rowName ? { ...row, values: [] } : row)),
    );
    const meta = apiFields.find((f) => f.value === newField);
    const inputType = meta?.filterInputType;
    // Skip API fetch for types that use hardcoded or range inputs
    const noApiFetch = !newField
      || inputType === 'datepicker'
      || inputType === 'number-range'
      || inputType === 'text'
      || (!inputType && ['boolean', 'date', 'number'].includes(meta?.type));
    if (!noApiFetch) {
      const isLookup = Boolean(meta?.lookup?.collection);
      fetchValues(newField, '', {
        dataSource: isLookup ? 'lookup' : meta?.dataSource,
        groupName: meta?.groupName,
        lookup: meta?.lookup,
      });
    }
  }

  return (
    <Drawer
      title={(
        <AppTypography variant="card-title" color="primary">
          Filters
        </AppTypography>
      )}
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
      rootClassName="dynamic-field-filter"
      maskClosable={false}
      footer={(
        <Space className="dynamic-field-filter__footer">
          <AppButton onClick={handleReset}>Reset</AppButton>
          <AppButton variant="primary" onClick={handleApply} disabled={hasNoCompleteRow}>Apply</AppButton>
        </Space>
      )}
    >

      {fieldsError && (
        <Alert
          type="error"
          message="Could not load filter fields"
          description={fieldsError}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Spin spinning={fieldsLoading} tip="Loading fields…">
        <Form
          form={form}
          layout="vertical"
          className="dynamic-field-filter__form"
          initialValues={{ filters: [defaultFilterRow] }}
        >
          <Form.List name="filters">
            {(formFields, { add, remove }) => (
              <Space direction="vertical" className="dynamic-field-filter__rows">
                {formFields.map((formField, index) => {
                  const selectedField = filterRows[index]?.field;
                  const selectedOperator = filterRows[index]?.operator;

                  const selectedFieldMeta = apiFields.find(
                    (f) => f.value === selectedField
                  );

                  const isLocked = selectedFieldMeta?.isDefaultFilter === true;
                  const disableValues = !selectedField
                    || selectedOperator === 'isEmpty'
                    || selectedOperator === 'notEmpty';
                  const selectedFieldType = selectedFieldMeta?.type ?? 'text';
                  const selectedFilterInputType = selectedFieldMeta?.filterInputType;
                  const valueOptions = valuesByField[selectedField] ?? [];
                  const valuesLoading = loadingFields.has(selectedField);

                  // Default (admin-configured) fields are pinned rows; only the
                  // remaining fields can be picked when adding a new row, and a
                  // field already used in another row can't be picked again.
                  // Values are coerced to strings since the API can return the
                  // field key as either a string or a number.
                  const usedFields = new Set(
                    filterRows
                      .filter((_, i) => i !== index)
                      .map((row) => row?.field)
                      .filter(Boolean)
                      .map(String),
                  );
                  const fieldOptions = isLocked
                    ? apiFields.filter((f) => f.value === selectedField)
                    : addableFields.filter((f) => !usedFields.has(String(f.value)));

                  return (
                    <Space
                      key={formField.key}
                      direction="vertical"
                      className="dynamic-field-filter__row"
                    >
                      {formFields.length > 1 && !isLocked && (
                        <AppButton
                          variant="danger"
                          htmlType="button"
                          icon={<DeleteOutlined />}
                          aria-label="Remove filter row"
                          className="dynamic-field-filter__remove"
                          onClick={() => remove(formField.name)}
                        />
                      )}

                      {index > 0 && (
                        <Flex justify="center">
                          <Form.Item
                            name={[formField.name, 'operator']}
                            rules={[{ required: true, message: 'Select condition' }]}
                            className="dynamic-field-filter__operator"
                          >
                            <Select options={operatorOptions} placeholder="Condition" />
                          </Form.Item>
                        </Flex>
                      )}

                      <Flex align="end" gap="middle" wrap>
                        <Form.Item
                          label={<AppTypography variant="label" color="secondary">Field</AppTypography>}
                          style={{ flex: 1, minWidth: 220 }}
                        >
                          {isLocked ? (
                            <>
                              <AppTypography variant="body" className="dynamic-field-filter__locked-field">
                                {selectedFieldMeta?.label ?? selectedField}
                              </AppTypography>
                              <Form.Item name={[formField.name, 'field']} noStyle>
                                <Input type="hidden" />
                              </Form.Item>
                            </>
                          ) : (
                            <Form.Item
                              name={[formField.name, 'field']}
                              rules={[{ required: true, message: 'Select field' }]}
                              noStyle
                            >
                              <Select
                                allowClear
                                showSearch
                                placeholder="Select field"
                                options={fieldOptions}
                                optionFilterProp="label"
                                loading={fieldsLoading}
                                onChange={(val) => handleFieldChange(formField.name, val)}
                              />
                            </Form.Item>
                          )}
                        </Form.Item>

                        <Form.Item
                          label={<AppTypography variant="label" color="secondary">Values</AppTypography>}
                          name={[formField.name, 'values']}
                          style={{ flex: 1, minWidth: 220 }}
                        >
                          {renderValuesInput({
                            filterInputType: selectedFilterInputType,
                            fieldType: selectedFieldType,
                            disabled: disableValues,
                            loading: valuesLoading,
                            valueOptions,
                            onSearch: (search) => {
                              if (selectedField) {
                                const isLookup = Boolean(selectedFieldMeta?.lookup?.collection);
                                fetchValues(selectedField, search, {
                                  dataSource: isLookup ? 'lookup' : selectedFieldMeta?.dataSource,
                                  groupName: selectedFieldMeta?.groupName,
                                  lookup: selectedFieldMeta?.lookup,
                                });
                              }
                            },
                            onOpen: (visible) => {
                              if (visible && selectedField) {
                                const isLookup = Boolean(selectedFieldMeta?.lookup?.collection);
                                fetchValues(selectedField, '', {
                                  dataSource: isLookup ? 'lookup' : selectedFieldMeta?.dataSource,
                                  groupName: selectedFieldMeta?.groupName,
                                  lookup: selectedFieldMeta?.lookup,
                                });
                              }
                            },
                          })}
                        </Form.Item>
                      </Flex>
                    </Space>
                  );
                })}

                <AppButton
                  htmlType="button"
                  variant="soft"
                  icon={<PlusOutlined />}
                  disabled={addableFields.length > 0 && filterRows.filter((r) => r?.field).length >= addableFields.length}
                  onClick={() => add(defaultFilterRow)}
                >
                  Add Row
                </AppButton>
              </Space>
            )}
          </Form.List>
        </Form>
      </Spin>

    </Drawer>
  );
}
